<template>
  <div>
    <v-dialog v-model="show" max-width="1024" @click:outside="closeModal" @keydown.esc="closeModal">
      <v-card>
        <v-card-title class="justify-center third">
          <span class="white--text">Historique des emails envoyés</span>
        </v-card-title>
        <v-card-text>
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn class="third white--text" @click="toggleDialog"> {{ year }}</v-btn>
            <v-dialog v-model="dateDialog" class="pa-0" width="290">
              <v-card class="d-flex flex-column align-end pa-0">
                <v-card-text class="d-flex justify-center pa-0">
                  <v-date-picker
                    ref="datePickerRef"
                    color="third"
                    :active-picker.sync="activePicker"
                    @click:year="pickDate"
                  />
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-col>
          <v-data-table
            :headers="headers"
            :items="items"
            :loading="loading ? 'third' : false"
            :sort-by="['date']"
          >
            <template #item.date="{ item }">
              {{ format(item.date * 1000, "'Le' dd/MM/yyyy à HH'h'mm'") }}
            </template>
            <template #item.actions="{ item }">
              <v-btn v-if="item.actions" text small @click="openConfirmationModal(item)">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
              <span v-else> - </span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <Modal v-if="showConfirmationModal" :show="showConfirmationModal" max-width="500" :close="closeConfirmationModal">
      <template #title>
        <div class="col-12 d-flex justify-center pa-0 third--text">
          <h3>Renvoyer l'email</h3>
        </div>
      </template>
      <template #text>
        <div class="d-flex flex flex-column justify-center align-center overflow-y-auto">
          <span>Êtes-vous sûr de vouloir renvoyer l'email ? </span>
          <span>L'email sera envoyé aux clients suivants:</span>

          <ul>
            <li v-for="customer in emailsToResend.recipients" :key="customer.uuid">
              {{ customer.email }}
            </li>
          </ul>
        </div>
      </template>
      <template #actions>
        <div class="d-flex col-12 justify-end pa-0">
          <v-btn color="grey" class="white--text ma-1" :loading="confirmationLoading" @click="closeConfirmationModal">Annuler</v-btn>
          <v-btn color="third" class="white--text ma-1" :loading="confirmationLoading" @click="resendEmail">Renvoyer</v-btn>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import { ref, computed, watch, onBeforeMount } from '@vue/composition-api';
import { getCustomersByUUids, sendEmail, getWorkspaceSentEmailsByPeriod } from '@/helpers/request';
import { format, fromUnixTime, getUnixTime, startOfYear, endOfYear } from 'date-fns';
import Modal from '@/components/common/Modal.vue';
import { sendNotification } from '@/helpers/utils';
import store from '@/store';

export default {
  name: 'SentEmailModal',
  components: { Modal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    emailTemplates: {
      type: Array,
      default: () => []
    }
  },
  setup (props, { emit }) {
    const loading = ref(false);
    const items = ref([]);
    const headers = ref([
      { text: 'Nom du template', value: 'name', width: '20%', align: 'center', sortable: false },
      { text: 'Objet du mail', value: 'title', width: '20%', align: 'center', sortable: false },
      { text: 'Date et heure d\'envoi', value: 'date', width: '25%', align: 'center' },
      { text: 'Nombre de destinataire', value: 'numberOfRecipients', width: '15%', align: 'center', sortable: false },
      { text: 'Statut', value: 'status', width: '10%', align: 'center', sortable: false },
      { text: 'Action', value: 'actions', width: '10%', align: 'center', sortable: false }
    ]);

    const closeModal = () => {
      emit('closeModal');
    };

    const showConfirmationModal = ref(false);
    const confirmationLoading = ref(false);
    const emailsToResend = ref({});

    const openConfirmationModal = async (item) => {
      showConfirmationModal.value = true;
      confirmationLoading.value = true;
      emailsToResend.value.templateUuid = item.templateUuid;
      const failedEmails = item.individualEmails.filter(email => email.status === 'FAILED');
      const response = await getCustomersByUUids(failedEmails.map(email => email.customerUuid));
      if (response?.success && response?.data?.items?.length) {
        emailsToResend.value.templateUuid = item.templateUuid;
        emailsToResend.value.recipients = response.data.items.filter(c => c).map(c => ({ uuid: c.uuid, email: c.email }))
      }
      confirmationLoading.value = false;
    };

    const closeConfirmationModal = () => {
      emailsToResend.value = {};
      showConfirmationModal.value = false;
    };

    const resendEmail = async () => {
      confirmationLoading.value = true;
      const response = await sendEmail({ emailUuid: emailsToResend.value.templateUuid, recipients: emailsToResend.value.recipients, workspace: store.state.workspace.code });
      confirmationLoading.value = false;
      if (response.success) {
        sendNotification('success', 'Email renvoyé avec succès');
        closeConfirmationModal();
        closeModal();
        emit('refreshEmails');
      } else {
        sendNotification('error', 'Erreur lors du renvoi de l\'email');
      }
    };

    const filterEmailHistory = (email) => {
      if(!email.sendHistory || !email.sendHistory.length) {
        return [{ts: email.lastEmailDate, status: 'SENT'}];
      }
      const sortedEmailHistory = [...email.sendHistory].sort((a, b) => a.ts - b.ts);
      const lastEmailHistory = sortedEmailHistory[sortedEmailHistory.length - 1];

      if(lastEmailHistory.status === 'SENT') {
        return sortedEmailHistory.filter(emailHistory => emailHistory.status === 'SENT');
      }

      let lastSuccessfullEmailIndex = -1;

      for (let i = sortedEmailHistory.length - 1; i >= 0; i -= 1) {
        if (sortedEmailHistory[i].status === 'SENT') {
          lastSuccessfullEmailIndex = i;
          break;
        }
      }
           
      if(lastSuccessfullEmailIndex === -1) {
        return sortedEmailHistory;
      }

      return sortedEmailHistory.filter((emailHistory, index) => !(index < lastSuccessfullEmailIndex && emailHistory.status === 'FAILED'));
    }

    const computeEmailsByTemplate = (allEmailsRequests) => {
      const emailsByTemplate = {};
      (allEmailsRequests || []).forEach(emailRequest => {
        const template = props.emailTemplates.find(template => template.uuid === emailRequest.emailUuid);

        if(!template) {
          return;
        }

        const emailsHistory = filterEmailHistory(emailRequest);

        emailsHistory.forEach(emailHistory => {
          const dateFormatted = format(fromUnixTime(emailHistory.ts), 'yyyy-MM-dd HH:mm');
          const groupTemplateAndDate = `${template.uuid}-${dateFormatted}`;
          if(!emailsByTemplate[groupTemplateAndDate]) {
            emailsByTemplate[groupTemplateAndDate] = {
              templateUuid: template.uuid,
              name: template.name,
              title: template.subject,
              date: emailHistory.ts,
              numberOfRecipients: 0,
              individualEmails: []
            };
          }
          emailsByTemplate[groupTemplateAndDate].individualEmails.push({customerUuid: emailRequest.customerUuid, status: emailHistory.status});
          emailsByTemplate[groupTemplateAndDate].numberOfRecipients++;
        });
      });
      Object.values(emailsByTemplate).forEach(email => {
        /* to handle old emails that don't have a status */
        if(email.individualEmails.every(email => !email.status || email.status === 'SENT')) {
          email.status = 'Envoyé';
          email.actions = false;
          return;
        }

        if(email.individualEmails.every(email => email.status === 'FAILED')) {
          email.status = 'Erreur';
          email.actions = true;
          return;
        }

        email.status = 'Envoyé / Erreur';
        email.actions = true;
      });
      return Object.values(emailsByTemplate);
    };

    const getWorkspaceSentEmails = async () => {
      const dateYear = new Date(`${year.value}-01-02`);
      const start = getUnixTime(startOfYear(dateYear));
      const end = getUnixTime(endOfYear(dateYear));

      loading.value = true;
      const response = await getWorkspaceSentEmailsByPeriod(start, end);
      loading.value = false;

      if(!response.success) {
        sendNotification('error', 'Erreur lors de la récupération des emails envoyés');
        return;
      }

      items.value = computeEmailsByTemplate(response.data.items);
    };

    const selectedDate = computed(() => store.getters.selectedDate);
    const year = ref(format(new Date(selectedDate.value), 'yyyy'));

    const activePicker = ref(null);
    const datePickerRef = ref(null);
    const dateDialog = ref(false);

    const toggleDialog = () => {
      dateDialog.value = !dateDialog.value;
    };

    const pickDate = async date => {
      year.value = parseInt(date);
      activePicker.value = null;
      dateDialog.value = false;
      await getWorkspaceSentEmails();
    };

    watch(activePicker, val => {
      if (val === 'MONTH') {
        activePicker.value = 'DATE';
        return;
      }

      if (val === 'DATE') {
        activePicker.value = 'YEAR';
        datePickerRef.value.yearClick(year.value);
      }
    });

    onBeforeMount(async () => {
      await getWorkspaceSentEmails();
    });

    return { headers, items, loading, closeModal, showConfirmationModal, closeConfirmationModal, openConfirmationModal, resendEmail, emailsToResend, format, confirmationLoading, year, pickDate, activePicker, datePickerRef, dateDialog, toggleDialog };
  }
}
</script>

<style scoped>  </style>